import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../utils/api';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, Sparkles, Download, ShoppingCart, Library, FileText, CheckCircle2, XCircle, Eye } from 'lucide-react';
import { toast } from 'sonner';

const formatSGD = (cents) => `S$${(Number(cents || 0) / 100).toFixed(2)}`;

const MarketplaceBrowsePage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [levelFilter, setLevelFilter] = useState('');
  const [worksheets, setWorksheets] = useState([]);
  const [purchases, setPurchases] = useState({ purchases: [], worksheets: {} });
  const [loading, setLoading] = useState(true);
  const [buying, setBuying] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [ws, mine] = await Promise.all([
        api.marketplaceList(levelFilter || undefined),
        api.marketplaceMyPurchases(),
      ]);
      setWorksheets(ws.worksheets || []);
      setPurchases(mine);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load marketplace');
    } finally {
      setLoading(false);
    }
  }, [levelFilter]);

  useEffect(() => { refresh(); }, [refresh]);

  const purchase = async (w) => {
    setBuying(w.id);
    try {
      const { checkout_url } = await api.marketplaceCheckout(w.id, window.location.origin);
      window.location.href = checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to start checkout');
      setBuying(null);
    }
  };

  const purchasedIds = new Set(purchases.purchases.map((p) => p.worksheet_id));

  const downloadPurchased = (worksheetId) => {
    const token = localStorage.getItem('token');
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/marketplace/worksheets/${worksheetId}/pdf?token=${encodeURIComponent(token)}`;
    window.open(url, '_blank', 'noopener');
  };

  const openPreview = (worksheetId) => {
    const token = localStorage.getItem('token');
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/marketplace/worksheets/${worksheetId}/preview?token=${encodeURIComponent(token)}`;
    window.open(url, '_blank', 'noopener');
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-16">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <button onClick={() => navigate('/dashboard')} className="inline-flex items-center gap-2 text-slate-700 hover:text-slate-900" data-testid="marketplace-back">
            <ArrowLeft className="w-4 h-4" /> Dashboard
          </button>
          <div className="flex items-center gap-2 text-amber-700">
            <Sparkles className="w-4 h-4" />
            <span className="text-sm font-semibold">Tutors' Treasure Box</span>
          </div>
          <div className="text-sm text-slate-600 hidden sm:block">{user?.name}</div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 pt-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Marketplace</h1>
            <p className="text-slate-600 text-sm">Browse worksheets uploaded by verified PSLE tutors. Auto-graded when you're done.</p>
          </div>
          <Link to="/tutor/register" className="text-sm text-amber-700 hover:underline" data-testid="marketplace-become-tutor">
            Are you a tutor? Sell your worksheets →
          </Link>
        </div>

        <Tabs defaultValue="browse">
          <TabsList>
            <TabsTrigger value="browse" data-testid="marketplace-tab-browse"><ShoppingCart className="w-4 h-4 mr-2" /> Browse</TabsTrigger>
            <TabsTrigger value="library" data-testid="marketplace-tab-library"><Library className="w-4 h-4 mr-2" /> My library ({purchases.purchases.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="browse" className="mt-4">
            <div className="mb-3 flex items-center gap-3">
              <label className="text-sm text-slate-600">Level</label>
              <Select value={levelFilter || 'all'} onValueChange={(v) => setLevelFilter(v === 'all' ? '' : v)}>
                <SelectTrigger className="w-40" data-testid="marketplace-level-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All levels</SelectItem>
                  <SelectItem value="P4">P4</SelectItem>
                  <SelectItem value="P5">P5</SelectItem>
                  <SelectItem value="P6">P6</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {loading && <div className="text-center text-slate-500 py-8">Loading…</div>}
            {!loading && worksheets.length === 0 && (
              <Card className="p-8 text-center text-slate-500 rounded-2xl">No worksheets available yet. Check back soon!</Card>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {worksheets.map((w) => (
                <Card key={w.id} className="p-5 rounded-2xl border-2 hover:shadow-md transition" data-testid={`marketplace-card-${w.id}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <Badge className="bg-amber-100 text-amber-800 border-amber-200">{w.level}</Badge>
                    <span className="text-xs text-slate-500">{w.num_questions} MCQs</span>
                  </div>
                  <h3 className="font-semibold text-slate-900 text-lg leading-tight">{w.title}</h3>
                  <div className="text-xs text-slate-500 mt-1">by {w.tutor_name}</div>
                  {w.description && <p className="text-sm text-slate-600 mt-2 line-clamp-3">{w.description}</p>}
                  <div className="mt-4 flex items-end justify-between gap-2">
                    <div className="text-2xl font-bold text-slate-900">{formatSGD(w.price_cents)}</div>
                    <div className="flex gap-2">
                      {w.has_preview && !purchasedIds.has(w.id) && (
                        <Button size="sm" variant="outline" onClick={() => openPreview(w.id)} data-testid={`marketplace-preview-${w.id}`}>
                          <Eye className="w-4 h-4 mr-1" /> Preview
                        </Button>
                      )}
                      {purchasedIds.has(w.id) ? (
                        <Button size="sm" onClick={() => downloadPurchased(w.id)} className="bg-emerald-600 hover:bg-emerald-700" data-testid={`marketplace-download-${w.id}`}>
                          <Download className="w-4 h-4 mr-2" /> Download
                        </Button>
                      ) : (
                        <Button size="sm" disabled={buying === w.id} onClick={() => purchase(w)} className="bg-amber-600 hover:bg-amber-700" data-testid={`marketplace-buy-${w.id}`}>
                          {buying === w.id ? 'Redirecting…' : 'Buy now'}
                        </Button>
                      )}
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="library" className="mt-4">
            {purchases.purchases.length === 0 && (
              <Card className="p-8 text-center text-slate-500 rounded-2xl">Your purchased worksheets will appear here.</Card>
            )}
            <div className="space-y-3">
              {purchases.purchases.map((p) => {
                const w = purchases.worksheets[p.worksheet_id];
                return (
                  <Card key={p.id} className="p-4 rounded-2xl" data-testid={`library-purchase-${p.id}`}>
                    <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between">
                      <div className="flex-1">
                        <div className="text-xs text-slate-500">Purchased {new Date(p.paid_at || p.created_at).toLocaleDateString()}</div>
                        <div className="font-semibold text-slate-900">{w?.title || 'Worksheet'}</div>
                        {w && <div className="text-xs text-slate-500">{w.level} • {w.num_questions} qns • by {w.tutor_name}</div>}
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => downloadPurchased(p.worksheet_id)} data-testid={`library-download-${p.id}`}>
                          <FileText className="w-4 h-4 mr-2" /> Download
                        </Button>
                        <Button size="sm" onClick={() => navigate(`/marketplace/grade/${p.id}`)} className="bg-emerald-600 hover:bg-emerald-700" data-testid={`library-grade-${p.id}`}>
                          Grade my answers
                        </Button>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export const MarketplaceSuccessPage = () => {
  const [status, setStatus] = useState('checking');
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sid = params.get('session_id');
    if (!sid) { setStatus('error'); return; }

    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const r = await api.marketplaceCheckoutStatus(sid);
        if (r.payment_status === 'paid') { setStatus('paid'); return; }
        if (attempts >= 10) { setStatus('slow'); return; }
        setTimeout(poll, 1500);
      } catch (e) {
        setStatus('error');
      }
    };
    poll();
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-slate-50">
      <Card className="p-8 text-center max-w-md rounded-2xl">
        {status === 'checking' && <><div className="animate-pulse text-slate-600">Confirming your purchase…</div></>}
        {status === 'paid' && (
          <>
            <CheckCircle2 className="w-14 h-14 text-emerald-600 mx-auto mb-3" />
            <h2 className="text-2xl font-bold text-slate-900">Payment successful!</h2>
            <p className="text-slate-600 mt-2">Your worksheet is now in your library.</p>
            <Button onClick={() => navigate('/marketplace')} className="mt-4 bg-amber-600 hover:bg-amber-700" data-testid="marketplace-success-continue">Go to library</Button>
          </>
        )}
        {status === 'slow' && (
          <>
            <div className="text-slate-700">Still confirming with Stripe — it should appear in your library shortly.</div>
            <Button onClick={() => navigate('/marketplace')} className="mt-4">Open library</Button>
          </>
        )}
        {status === 'error' && (
          <>
            <XCircle className="w-14 h-14 text-rose-600 mx-auto mb-3" />
            <div className="text-slate-700">We couldn't confirm the payment. If you were charged, it will still appear in your library once Stripe notifies us.</div>
            <Button onClick={() => navigate('/marketplace')} className="mt-4">Back to marketplace</Button>
          </>
        )}
      </Card>
    </div>
  );
};

export const MarketplaceCancelPage = () => {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-slate-50">
      <Card className="p-8 text-center max-w-md rounded-2xl">
        <h2 className="text-2xl font-bold text-slate-900">Purchase cancelled</h2>
        <p className="text-slate-600 mt-2">No charge was made. Feel free to keep browsing.</p>
        <Button onClick={() => navigate('/marketplace')} className="mt-4 bg-amber-600 hover:bg-amber-700">Back to marketplace</Button>
      </Card>
    </div>
  );
};

export default MarketplaceBrowsePage;
